interface LogOptions {
  level?: 'log' | 'warn' | 'error' | 'debug';
  context?: string;
  error?: Error | unknown;
  data?: Record<string, unknown>;
}

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;

export function log(message: string, options: LogOptions = {}) {
  const { level = 'log', context, error, data } = options;
  
  // In production, only log warnings and errors
  if (isProd && (level === 'debug' || level === 'log')) return;
  
  const timestamp = new Date().toISOString();
  const contextStr = context ? `[${context}]` : '';
  const errorMessage = error instanceof Error ? error.message : String(error || '');
  
  // Only include stack trace in development or for errors
  const includeData = isDev || level === 'error';
  const logData = includeData && data ? JSON.stringify(data, null, 2) : '';
  
  const logMessage = `${timestamp} ${contextStr} ${message} ${errorMessage} ${logData}`.trim();
  
  switch (level) {
    case 'warn':
      console.warn(logMessage);
      break;
    case 'error':
      console.error(logMessage);
      break;
    case 'debug':
      if (isDev) console.debug(logMessage);
      break;
    default:
      if (isDev) console.log(logMessage);
  }
}

export function logError(message: string, error?: unknown, context?: string) {
  log(message, {
    level: 'error',
    context,
    error,
    data: error instanceof Error ? { stack: error.stack } : undefined,
  });
}

export function logWarn(message: string, error?: unknown, context?: string) {
  log(message, {
    level: 'warn',
    context,
    error,
    data: error instanceof Error ? { stack: error.stack } : undefined,
  });
}

export function logWarning(message: string, context?: string) {
  log(message, { level: 'warn', context });
}

export function logDebug(message: string, data?: Record<string, unknown>, context?: string) {
  log(message, { level: 'debug', context, data });
}
